import { Avatar, AvatarImage, Button, Separator, toast } from "@mindstonehq/ui";
import { Link, Outlet } from "react-router";
import React from "react";
import { graphql } from "~/graphql/codegen";
import { useMutation } from "@apollo/client/react";
import metrics from "~/utils/metrics";
import { useCopyToClipboard } from "react-use";
import { CheckIcon, ShareIcon } from "lucide-react";
import type {
  UnfollowProfileMutation,
  UnfollowProfileMutationVariables,
  FollowProfileMutation,
  FollowProfileMutationVariables,
} from "~/graphql/codegen/graphql";
import useAuthCheck from "~/hooks/check-authenticated";
import { LinkedinIcon } from "../icons";
import ProfileQRCode from "./profile-qr-code-dalog";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { displayNameOf } from "~/utils/display-name";

const FOLLOW_PROFILE_MUTATION = graphql(`
  mutation followProfile($userID: ID!) {
    followProfile(userID: $userID)
  }
`);

const UNFOLLOW_PROFILE_MUTATION = graphql(`
  mutation unfollowProfile($userID: ID!) {
    unfollowProfile(userID: $userID)
  }
`);

export default function ProfileTop({
  profile,
  chipsSlot,
}: {
  profile: {
    id: string;
    profilePictureURL: string;
    displayName: string;
    tagLine?: string | null;
    followers: { total: number };
    followings: { total: number };
    username: string;
    mine: boolean;
    profileUrl?: string | null;
    following: boolean;
  };
  chipsSlot?: React.ReactNode;
}) {
  const { t } = useLingui();
  const [following, setFollowing] = React.useState<boolean>(
    !!profile.following,
  );
  const [copyState, copyToClipboard] = useCopyToClipboard();
  const [unfollowProfile] = useMutation<
    UnfollowProfileMutation,
    UnfollowProfileMutationVariables
  >(UNFOLLOW_PROFILE_MUTATION);
  const [followProfile] = useMutation<
    FollowProfileMutation,
    FollowProfileMutationVariables
  >(FOLLOW_PROFILE_MUTATION);
  const checkAuth = useAuthCheck();

  const handleFollow = () => {
    const authenticated = checkAuth(
      t(msg`You need to be logged in to follow users`),
      `${location.href}`,
      "dashboard_follow",
    );
    if (!authenticated) {
      return;
    }
    if (following) {
      unfollowProfile({
        variables: {
          userID: profile.id,
        },
      })
        .then(() => {
          setFollowing(false);
        })
        .catch((error) => {
          toast.error(t(msg`Error unfollowing user`), {
            description: t(msg`Please try again later.`),
          });
        });
    } else {
      followProfile({
        variables: {
          userID: profile.id,
        },
      })
        .then(() => {
          setFollowing(true);
        })
        .catch((error) => {
          toast.error(t(msg`Error following user`), {
            description: t(msg`Please try again later.`),
          });
        });
    }
  };

  const handleLinkedin = () => {
    metrics.track("did_tap_share_profile", {
      type: "linkedin",
      url: profile?.profileUrl,
    });
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        profile?.profileUrl || "",
      )}`,
      // eslint-disable-next-line lingui/no-unlocalized-strings -- window.open target
      "_blank",
    );
  };

  const handleShare = async () => {
    metrics.track("did_tap_share_profile", {
      type: "native",
      mine: profile.mine,
      url: profile?.profileUrl,
    });
    const shareData = {
      title: t(msg`${displayNameOf(profile)}'s profile on Mindstone`),
      text: t(msg`Get to know @${profile?.username}'s achievements and skills`),
      url: profile?.profileUrl || window.location.href,
    };
    if (navigator.canShare && navigator.canShare(shareData)) {
      await navigator.share(shareData).catch((err) => {
        console.error(err);
        copyToClipboard(profile?.profileUrl || "");
      });
    } else {
      copyToClipboard(profile?.profileUrl || "");
    }
  };

  return (
    <div className="flex flex-col sm:flex-row justify-center items-center justify-between gap-4 sm:gap-3">
      <div className="flex flex-col sm:flex-row justify-center items-center gap-3">
        <Avatar className="h-20 w-20">
          <AvatarImage src={profile?.profilePictureURL} />
        </Avatar>
        <div className="flex flex-col gap-1.5 text-center sm:text-left">
          <div className="flex flex-col pl-3">
            <h3>{profile.displayName}</h3>
            {profile.tagLine && <div>{profile.tagLine}</div>}
            {chipsSlot && <div className="mt-1.5">{chipsSlot}</div>}
          </div>
          <div className="flex flex-col sm:flex-row justify-center items-center">
            <div className="flex items-center">
              <Link
                to="followers"
                className="flex items-center gap-1 px-3 text-sm leading-none min-h-8 sm:min-h-5"
              >
                <Trans>
                  <span className="font-bold">{profile.followers.total}</span>{" "}
                  Followers
                </Trans>
              </Link>
              <Separator orientation="vertical" className="h-5" />
              <Link
                to="following"
                className="flex items-center gap-1 px-3 text-sm leading-none min-h-8 sm:min-h-5"
              >
                <Trans>
                  <span className="font-bold">{profile.followings.total}</span>{" "}
                  Following
                </Trans>
              </Link>
            </div>
            {profile.username && (
              <div className="flex items-center gap-3 text-sm text-placeholder-foreground">
                <div className="hidden sm:inline">&bull;</div>
                <div>@{profile.username}</div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col items-center gap-1.5 sm:self-end sm:items-end">
        {profile.mine && (
          <div className="text-xs text-muted-foreground">
            <Trans>Public profile preview</Trans>
          </div>
        )}
        <div className="flex items-center gap-2">
          {profile.mine ? (
            <Button asChild variant="secondary" size="sm">
              <Link to="/settings"><Trans>Edit Profile</Trans></Link>
            </Button>
          ) : (
            <Button
              variant={following ? "outline" : "secondary"}
              size="sm"
              onClick={handleFollow}
            >
              {following ? t(msg`Following`) : t(msg`Follow`)}
            </Button>
          )}
          {profile?.profileUrl && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                aria-label={t(msg`Share profile`)}
              >
                {copyState.value && <CheckIcon className={"h-4 w-4"} />}
                {!copyState.value && <ShareIcon className={"h-4 w-4"} />}
              </Button>
              {/* <Button variant="outline" size="sm" onClick={handleLinkedin}>
                <LinkedinIcon className="h-4 w-4 " />
              </Button> */}
            </>
          )}

          {profile.mine && (
            <div className="flex sm:hidden">
              <ProfileQRCode profile={profile} />
            </div>
          )}
        </div>
      </div>
      <Outlet />
    </div>
  );
}
